class OakCustomOrderFields extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = document.getElementById(this.dataset.formId || '');
    this.maxFileBytes = Number(this.dataset.maxFileMb || 20) * 1024 * 1024;
    this.artworkInput = this.querySelector('[data-oak-artwork]');
    this.additionalInputs = [...this.querySelectorAll('[data-oak-file]')];
    this.optionInputs = [...this.querySelectorAll('[data-oak-design-option]')];
    this.fileError = this.querySelector('[data-oak-file-error]');
    this.previewImage = this.querySelector('[data-oak-preview-image]');
    this.previewText = this.querySelector('[data-oak-preview-text]');
    this.generatedPreview = this.querySelector('[data-oak-generated-preview]');
    this.previewMessage = this.querySelector('[data-oak-preview-message]');
    this.controls = {
      text: this.querySelector('[data-oak-text]'),
      color: this.querySelector('[data-oak-text-color]'),
      textSize: this.querySelector('[data-oak-text-size]'),
      artScale: this.querySelector('[data-oak-art-scale]'),
      artX: this.querySelector('[data-oak-art-x]'),
      artY: this.querySelector('[data-oak-art-y]'),
    };

    this.imageObjectUrl = null;
    this.loadedImage = null;

    this.additionalInputs.forEach((input) => {
      input.addEventListener('change', () => this.validateFile(input));
    });

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.syncArtworkRequirement());
    });

    this.artworkInput?.addEventListener('change', () => this.loadArtwork());

    Object.values(this.controls).forEach((input) => {
      input?.addEventListener('input', () => {
        this.updatePreview();
        this.updateGeneratedPreview();
      });
    });

    this.form?.addEventListener('submit', (event) => {
      this.syncArtworkRequirement();
      if (!this.validateAllFiles()) {
        event.preventDefault();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.updateGeneratedPreview();
    }, true);

    this.syncArtworkRequirement();
    this.updatePreview();
  }

  disconnectedCallback() {
    if (this.imageObjectUrl) URL.revokeObjectURL(this.imageObjectUrl);
  }

  selectedOption() {
    return this.optionInputs.find((input) => input.checked)?.value || '';
  }

  syncArtworkRequirement() {
    if (!this.artworkInput) return;
    const option = this.selectedOption();
    const requiresArtwork = option === 'I have print-ready artwork' || option === 'I need minor artwork adjustments';
    this.artworkInput.required = requiresArtwork;
    this.artworkInput.setAttribute('aria-required', requiresArtwork ? 'true' : 'false');
  }

  validateFile(input) {
    if (!input?.files?.length) {
      input.setCustomValidity('');
      return true;
    }

    const file = input.files[0];
    if (file.size > this.maxFileBytes) {
      const maxMb = Math.round(this.maxFileBytes / 1024 / 1024);
      input.setCustomValidity(`Please choose a file smaller than ${maxMb} MB.`);
      this.showError(`“${file.name}” is too large. Please use a file smaller than ${maxMb} MB.`);
      return false;
    }

    input.setCustomValidity('');
    this.hideError();
    return true;
  }

  validateAllFiles() {
    return this.additionalInputs.every((input) => this.validateFile(input));
  }

  showError(message) {
    if (!this.fileError) return;
    this.fileError.textContent = message;
    this.fileError.dataset.visible = 'true';
  }

  hideError() {
    if (!this.fileError) return;
    this.fileError.textContent = '';
    this.fileError.dataset.visible = 'false';
  }

  loadArtwork() {
    if (!this.artworkInput?.files?.length || !this.validateFile(this.artworkInput)) {
      this.loadedImage = null;
      if (this.previewImage) this.previewImage.style.display = 'none';
      this.updateGeneratedPreview();
      return;
    }

    const file = this.artworkInput.files[0];
    if (!file.type.startsWith('image/')) {
      this.loadedImage = null;
      if (this.previewImage) this.previewImage.style.display = 'none';
      if (this.previewMessage) this.previewMessage.textContent = 'The file will be attached to the order. PDF and TIFF files do not display in this browser preview.';
      this.updateGeneratedPreview();
      return;
    }

    if (this.imageObjectUrl) URL.revokeObjectURL(this.imageObjectUrl);
    this.imageObjectUrl = URL.createObjectURL(file);

    const image = new Image();
    image.onload = () => {
      this.loadedImage = image;
      if (this.previewImage) {
        this.previewImage.src = this.imageObjectUrl;
        this.previewImage.alt = file.name;
        this.previewImage.style.display = 'block';
      }
      if (this.previewMessage) this.previewMessage.textContent = 'This is a placement preview only. Oak & Ember will verify print quality and sizing.';
      this.updatePreview();
      this.updateGeneratedPreview();
    };
    image.onerror = () => {
      this.loadedImage = null;
      this.showError('This image could not be previewed. It will still be attached if the file type is supported by Shopify.');
    };
    image.src = this.imageObjectUrl;
  }

  numberValue(input, fallback) {
    const value = Number(input?.value);
    return Number.isFinite(value) ? value : fallback;
  }

  updatePreview() {
    const scale = this.numberValue(this.controls.artScale, 55);
    const x = this.numberValue(this.controls.artX, 50);
    const y = this.numberValue(this.controls.artY, 45);
    const textSize = this.numberValue(this.controls.textSize, 30);
    const text = this.controls.text?.value || '';
    const color = this.controls.color?.value || '#111111';

    if (this.previewImage) {
      this.previewImage.style.width = `${scale}%`;
      this.previewImage.style.height = `${scale}%`;
      this.previewImage.style.left = `${x}%`;
      this.previewImage.style.top = `${y}%`;
    }

    if (this.previewText) {
      this.previewText.textContent = text;
      this.previewText.style.fontSize = `${Math.max(14, textSize * 0.55)}px`;
      this.previewText.style.color = color;
      this.previewText.style.display = text.trim() ? 'block' : 'none';
    }

    this.querySelectorAll('[data-oak-output]').forEach((output) => {
      const input = this.querySelector(`#${CSS.escape(output.dataset.oakOutput)}`);
      if (input) output.textContent = input.value;
    });
  }

  drawContainedImage(context, image, centerX, centerY, boxSize) {
    const ratio = Math.min(boxSize / image.naturalWidth, boxSize / image.naturalHeight);
    const width = image.naturalWidth * ratio;
    const height = image.naturalHeight * ratio;
    context.drawImage(image, centerX - width / 2, centerY - height / 2, width, height);
  }

  wrapText(context, text, x, y, maxWidth, lineHeight) {
    const words = text.trim().split(/\s+/);
    let line = '';
    const lines = [];
    words.forEach((word) => {
      const testLine = line ? `${line} ${word}` : word;
      if (context.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    });
    if (line) lines.push(line);
    const startY = y - ((lines.length - 1) * lineHeight) / 2;
    lines.slice(0, 5).forEach((value, index) => context.fillText(value, x, startY + index * lineHeight));
  }

  updateGeneratedPreview() {
    if (!this.generatedPreview) return;
    const text = this.controls.text?.value?.trim() || '';
    if (!this.loadedImage && !text) {
      this.generatedPreview.value = '';
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 1000;
    canvas.height = 1000;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);

    if (this.loadedImage) {
      const scale = this.numberValue(this.controls.artScale, 55) / 100;
      const x = this.numberValue(this.controls.artX, 50) / 100 * canvas.width;
      const y = this.numberValue(this.controls.artY, 45) / 100 * canvas.height;
      this.drawContainedImage(context, this.loadedImage, x, y, canvas.width * scale);
    }

    if (text) {
      const textSize = this.numberValue(this.controls.textSize, 30);
      context.fillStyle = this.controls.color?.value || '#111111';
      context.font = `800 ${Math.max(30, textSize * 1.7)}px Arial, sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      this.wrapText(context, text, canvas.width / 2, canvas.height * 0.84, canvas.width * 0.88, Math.max(48, textSize * 2));
    }

    try {
      const dataUrl = canvas.toDataURL('image/png');
      const [header, base64] = dataUrl.split(',');
      const mime = header.match(/:(.*?);/)?.[1] || 'image/png';
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const file = new File([bytes], `customer-design-preview-${Date.now()}.png`, { type: mime });
      const transfer = new DataTransfer();
      transfer.items.add(file);
      this.generatedPreview.files = transfer.files;
    } catch (error) {
      console.warn('Could not create the design preview file.', error);
    }
  }
}

if (!customElements.get('oak-custom-order-fields')) {
  customElements.define('oak-custom-order-fields', OakCustomOrderFields);
}
